import { Left, Right } from "functype/either";
import { Try } from "functype/try";
import { randomBytes } from "node:crypto";
import { basename, extname } from "node:path";
//#region src/constants.ts
const GRAPH_API_BASE = "https://graph.microsoft.com";
//#endregion
//#region src/utils/odata-helpers.ts
const buildODataQuery = (params) => {
	if (!params) return "";
	const parts = [];
	if (params.$select && params.$select.length > 0) parts.push(`$select=${params.$select.join(",")}`);
	if (params.$filter) parts.push(`$filter=${encodeURIComponent(params.$filter)}`);
	if (params.$expand && params.$expand.length > 0) parts.push(`$expand=${params.$expand.join(",")}`);
	if (params.$orderby) parts.push(`$orderby=${encodeURIComponent(params.$orderby)}`);
	if (params.$top !== void 0) parts.push(`$top=${params.$top}`);
	if (params.$skip !== void 0) parts.push(`$skip=${params.$skip}`);
	if (params.$search) parts.push(`$search="${encodeURIComponent(params.$search)}"`);
	if (params.$count) parts.push(`$count=true`);
	return parts.length > 0 ? `?${parts.join("&")}` : "";
};
const appendODataQuery = (path, queryString) => {
	if (!queryString) return path;
	return path.includes("?") ? `${path}&${queryString.slice(1)}` : `${path}${queryString}`;
};
//#endregion
//#region src/utils/pagination.ts
const MAX_PAGES = 50;
const fetchAllPages = async (fetchPage, initialUrl) => {
	const allItems = [];
	let currentUrl = initialUrl;
	let pageCount = 0;
	while (currentUrl && pageCount < MAX_PAGES) {
		const result = await fetchPage(currentUrl);
		if (result.isLeft()) return Left(result.value);
		const page = result.value;
		allItems.push(...page.value);
		currentUrl = page["@odata.nextLink"] ?? null;
		pageCount++;
	}
	return Right(allItems);
};
const parseJsonResponse = (text) => Try(() => JSON.parse(text)).fold(() => Left({
	type: "parse",
	message: "Failed to parse Microsoft Graph API response"
}), (data) => Right(data));
//#endregion
//#region src/graph-request.ts
const mapHttpError = async (response) => {
	const fallbackMessage = `Microsoft Graph API error: ${response.status} ${response.statusText}`;
	const { message, graphErrorCode } = await (async () => {
		try {
			var _errorBody$error, _errorBody$error2;
			const errorBody = await response.json();
			return {
				message: (errorBody === null || errorBody === void 0 || (_errorBody$error = errorBody.error) === null || _errorBody$error === void 0 ? void 0 : _errorBody$error.message) ?? fallbackMessage,
				graphErrorCode: errorBody === null || errorBody === void 0 || (_errorBody$error2 = errorBody.error) === null || _errorBody$error2 === void 0 ? void 0 : _errorBody$error2.code
			};
		} catch {
			return { message: fallbackMessage };
		}
	})();
	const retryAfter = response.headers.get("Retry-After");
	switch (response.status) {
		case 401: return Left({
			type: "auth",
			message,
			status: 401,
			graphErrorCode
		});
		case 403: return Left({
			type: "forbidden",
			message,
			status: 403,
			graphErrorCode
		});
		case 404: return Left({
			type: "not_found",
			message,
			status: 404,
			graphErrorCode
		});
		case 429: return Left({
			type: "throttle",
			message,
			status: 429,
			graphErrorCode,
			retryAfter: retryAfter ? parseInt(retryAfter, 10) : void 0
		});
		default: return Left({
			type: "api",
			message,
			status: response.status,
			graphErrorCode
		});
	}
};
const createGraphRequest = (auth, config = {}) => {
	const resolveVersion = config.defaultVersion ?? (() => "v1.0");
	const request = async (method, path, options) => {
		const tokenResult = await auth.getAccessToken();
		if (tokenResult.isLeft()) return Left({
			type: "auth",
			message: tokenResult.value.message
		});
		const token = tokenResult.value;
		const version = (options === null || options === void 0 ? void 0 : options.version) ?? resolveVersion();
		const queryString = buildODataQuery(options === null || options === void 0 ? void 0 : options.odataParams);
		const url = `${GRAPH_API_BASE}/${version}${appendODataQuery(path, queryString)}`;
		try {
			const fetchOptions = {
				method,
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": (options === null || options === void 0 ? void 0 : options.contentType) ?? "application/json",
					...(options === null || options === void 0 ? void 0 : options.headers) ?? {}
				}
			};
			if ((options === null || options === void 0 ? void 0 : options.body) !== void 0 && (method === "POST" || method === "PUT" || method === "PATCH")) fetchOptions.body = typeof options.body === "string" ? options.body : JSON.stringify(options.body);
			const response = await fetch(url, fetchOptions);
			if (!response.ok) return mapHttpError(response);
			if (response.status === 204) return Right({});
			const text = await response.text();
			if (!text || text.trim() === "") return Right({});
			if ((options === null || options === void 0 ? void 0 : options.responseType) === "text") return Right(text);
			return parseJsonResponse(text);
		} catch (error) {
			return Left({
				type: "network",
				message: `Network error: ${error instanceof Error ? error.message : String(error)}`
			});
		}
	};
	const requestPaginated = async (path, options) => {
		const version = (options === null || options === void 0 ? void 0 : options.version) ?? resolveVersion();
		const queryString = buildODataQuery(options === null || options === void 0 ? void 0 : options.odataParams);
		const initialUrl = `${GRAPH_API_BASE}/${version}${appendODataQuery(path, queryString)}`;
		return fetchAllPages(async (url) => {
			const tokenResult = await auth.getAccessToken();
			if (tokenResult.isLeft()) return Left({
				type: "auth",
				message: tokenResult.value.message
			});
			const token = tokenResult.value;
			try {
				const response = await fetch(url, { headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
					...(options === null || options === void 0 ? void 0 : options.headers) ?? {}
				} });
				if (!response.ok) return mapHttpError(response);
				const text = await response.text();
				return parseJsonResponse(text);
			} catch (error) {
				return Left({
					type: "network",
					message: `Network error: ${error instanceof Error ? error.message : String(error)}`
				});
			}
		}, initialUrl);
	};
	return {
		request,
		requestPaginated
	};
};
//#endregion
//#region src/upload/upload.ts
const SIMPLE_UPLOAD_LIMIT = 4194304;
const MAX_UPLOAD_SIZE = 262144e3;
const CHUNK_SIZE = 10485760;
const describeFetchError = (error) => {
	if (!(error instanceof Error)) return { message: String(error) };
	const { cause } = error;
	if (cause instanceof Error) {
		const { code } = cause;
		const codeStr = typeof code === "string" ? code : void 0;
		return {
			message: `${error.message} (cause: ${cause.message}${codeStr ? `, code: ${codeStr}` : ""})`,
			...codeStr ? { code: codeStr } : {}
		};
	}
	if (cause !== void 0) return { message: `${error.message} (cause: ${String(cause)})` };
	return { message: error.message };
};
const parseGraphError = async (response) => {
	try {
		var _body$error;
		const body = await response.json();
		if ((_body$error = body.error) === null || _body$error === void 0 ? void 0 : _body$error.message) return body.error.message;
	} catch {}
	return `${response.status} ${response.statusText}`;
};
const toGraphApiError = async (response, prefix) => {
	return {
		type: "api",
		message: `${prefix}: ${await parseGraphError(response)}`,
		status: response.status
	};
};
const decodeBase64Upload = (rawBuffer) => Buffer.from(rawBuffer.toString("utf-8").replace(/\s/g, ""), "base64");
const simpleUpload = async (apiBase, path, accessToken, buffer, contentType, conflictBehavior) => {
	const url = `${apiBase}${path}${path.includes("?") ? "&" : "?"}@microsoft.graph.conflictBehavior=${conflictBehavior}`;
	try {
		const response = await fetch(url, {
			method: "PUT",
			headers: {
				Authorization: `Bearer ${accessToken}`,
				"Content-Type": contentType,
				"Content-Length": String(buffer.length)
			},
			body: new Uint8Array(buffer)
		});
		if (!response.ok) return Left(await toGraphApiError(response, "Upload failed"));
		const item = await response.json();
		return Right(item);
	} catch (error) {
		const { message } = describeFetchError(error);
		console.error(`[Upload] simple PUT to Graph threw: ${message}`);
		return Left({
			type: "network",
			message: `Network error during upload: ${message}`
		});
	}
};
const sessionUpload = async (apiBase, path, accessToken, buffer, conflictBehavior) => {
	const sessionUrl = `${apiBase}${path.replace(/:\/?content$/i, ":/createUploadSession")}`;
	try {
		const createResponse = await fetch(sessionUrl, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${accessToken}`,
				"Content-Type": "application/json"
			},
			body: JSON.stringify({ item: { "@microsoft.graph.conflictBehavior": conflictBehavior } })
		});
		if (!createResponse.ok) return Left(await toGraphApiError(createResponse, "Failed to create upload session"));
		const session = await createResponse.json();
		return uploadChunks(session.uploadUrl, buffer, 0);
	} catch (error) {
		const { message } = describeFetchError(error);
		console.error(`[Upload] createUploadSession to Graph threw: ${message}`);
		return Left({
			type: "network",
			message: `Network error creating upload session: ${message}`
		});
	}
};
const uploadChunks = async (uploadUrl, buffer, offset) => {
	const totalSize = buffer.length;
	if (offset >= totalSize) return Left({
		type: "api",
		message: "Upload completed but no DriveItem response received"
	});
	const end = Math.min(offset + CHUNK_SIZE, totalSize);
	const chunk = buffer.subarray(offset, end);
	try {
		const chunkResponse = await fetch(uploadUrl, {
			method: "PUT",
			headers: {
				"Content-Length": String(chunk.length),
				"Content-Range": `bytes ${offset}-${end - 1}/${totalSize}`
			},
			body: new Uint8Array(chunk)
		});
		if (!chunkResponse.ok) {
			await fetch(uploadUrl, { method: "DELETE" }).catch(() => {});
			return Left(await toGraphApiError(chunkResponse, `Upload chunk failed at byte ${offset}`));
		}
		if (chunkResponse.status === 200 || chunkResponse.status === 201) {
			const item = await chunkResponse.json();
			return Right(item);
		}
		return uploadChunks(uploadUrl, buffer, offset + CHUNK_SIZE);
	} catch (error) {
		const { message } = describeFetchError(error);
		console.error(`[Upload] chunk PUT at byte ${offset} threw: ${message}`);
		return Left({
			type: "network",
			message: `Network error uploading chunk at byte ${offset}: ${message}`
		});
	}
};
//#endregion
//#region src/upload/upload-ticket.ts
const DEFAULT_TTL_MS = 6e5;
const TICKET_PREFIX = "upl_";
const store = /* @__PURE__ */ new Map();
const sweep = (now) => {
	for (const [id, ticket] of store) if (ticket.expiresAt <= now) store.delete(id);
};
const isUploadTicket = (value) => value.startsWith(TICKET_PREFIX);
const mintUploadTicket = (token, ttlMs = DEFAULT_TTL_MS, now = Date.now()) => {
	sweep(now);
	const id = `${TICKET_PREFIX}${randomBytes(24).toString("base64url")}`;
	store.set(id, {
		token,
		expiresAt: now + ttlMs
	});
	return id;
};
const resolveUploadTicket = (id, now = Date.now()) => {
	const ticket = store.get(id);
	if (!ticket) return void 0;
	if (ticket.expiresAt <= now) {
		store.delete(id);
		return;
	}
	return ticket.token;
};
//#endregion
//#region src/utils/upload-helpers.ts
const CONTENT_TYPE_MAP = {
	".pdf": "application/pdf",
	".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	".pptx": "application/vnd.openxmlformats-officedocument.presentationml.presentation",
	".doc": "application/msword",
	".xls": "application/vnd.ms-excel",
	".ppt": "application/vnd.ms-powerpoint",
	".txt": "text/plain",
	".md": "text/markdown",
	".csv": "text/csv",
	".json": "application/json",
	".xml": "application/xml",
	".html": "text/html",
	".htm": "text/html",
	".png": "image/png",
	".jpg": "image/jpeg",
	".jpeg": "image/jpeg",
	".gif": "image/gif",
	".svg": "image/svg+xml",
	".zip": "application/zip",
	".mp4": "video/mp4",
	".mp3": "audio/mpeg"
};
const filenameFromPath = (path) => {
	const colonPathMatch = /:\/([^:]+):\/content/i.exec(path);
	return (colonPathMatch === null || colonPathMatch === void 0 ? void 0 : colonPathMatch[1]) ? basename(colonPathMatch[1]) : void 0;
};
const resolveUploadContentType = (explicit, filename) => {
	if (explicit) return explicit;
	if (!filename) return "application/octet-stream";
	const ext = extname(filename).toLowerCase();
	return CONTENT_TYPE_MAP[ext] ?? "application/octet-stream";
};
const formatBytes = (bytes) => {
	if (bytes === 0) return "0 B";
	const units = [
		"B",
		"KB",
		"MB",
		"GB"
	];
	const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
	return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};
//#endregion
export { CONTENT_TYPE_MAP, GRAPH_API_BASE, MAX_UPLOAD_SIZE, SIMPLE_UPLOAD_LIMIT, appendODataQuery, buildODataQuery, createGraphRequest, decodeBase64Upload, describeFetchError, fetchAllPages, filenameFromPath, formatBytes, isUploadTicket, mapHttpError, mintUploadTicket, parseJsonResponse, resolveUploadContentType, resolveUploadTicket, sessionUpload, simpleUpload };

//# sourceMappingURL=index.js.map